import React, { useState, useEffect } from "react";
import { CancelButton, FormButton } from "../../../../components/Button";
import { FormFieldset, Input, Textarea } from "../../../../components/Form";
import CustomDropdown from "../../../../components/DropDown";
import Loader from "../../../../components/Loader";
import ApiService from "../../../../service/ApiService";
import { apiPaths } from "../../../../service/apiPaths";
import toast from "react-hot-toast";
import { ILead } from "../Lead.props";

interface ConvertToClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadData: ILead | null;
}

interface ClientFormData {
  name: string;
  email: string;
  phone: string;
  companyName: string;
  description: string;
  linkedin: string;
  platform: string;
  interest: string;
  services: string;
  outputStatus: string;
  clientType: string;
  industry: string;
  annualRevenue: string;
  employeeCount: string;
  timezone: string;
  preferredLanguage: string;
  notes: string;
  tags: string;
  assignedTo: string;
  convertedFrom: string;
}

const ConvertToClientModal: React.FC<ConvertToClientModalProps> = ({
  isOpen,
  onClose,
  leadData,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ClientFormData>({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    description: "",
    linkedin: "",
    platform: "",
    interest: "",
    services: "",
    outputStatus: "",
    clientType: "",
    industry: "",
    annualRevenue: "",
    employeeCount: "",
    timezone: "",
    preferredLanguage: "",
    notes: "",
    tags: "",
    assignedTo: "",
    convertedFrom: "",
  });

  const [platforms, setPlatforms] = useState<any[]>([]);
  const [interests, setInterests] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [outputStatuses, setOutputStatuses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  // Load form data from lead when modal opens
  useEffect(() => {
    if (isOpen && leadData) {
      setFormData({
        name: leadData.name || "",
        email: leadData.email || "",
        phone: leadData.phone || "",
        companyName: leadData.companyName || "",
        description: leadData.description || "",
        linkedin: leadData.linkedin || "",
        platform: leadData.platform?._id || "",
        interest: leadData.interest?._id || "",
        services: leadData.services?._id || "",
        outputStatus: leadData.outputStatus?._id || "",
        clientType: "Prospect",
        industry: "",
        annualRevenue: "",
        employeeCount: "",
        timezone: "UTC",
        preferredLanguage: "English",
        notes: leadData.comments || "",
        tags: "",
        assignedTo: leadData.userId?._id || "",
        convertedFrom: leadData._id || "",
      });
    }
  }, [leadData]); // Remove isOpen from dependencies to prevent resetting on every open

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        companyName: "",
        description: "",
        linkedin: "",
        platform: "",
        interest: "",
        services: "",
        outputStatus: "",
        clientType: "",
        industry: "",
        annualRevenue: "",
        employeeCount: "",
        timezone: "",
        preferredLanguage: "",
        notes: "",
        tags: "",
        assignedTo: "",
        convertedFrom: "",
      });
    }
  }, [isOpen]);

  // Load dropdown data
  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
    }
  }, [isOpen]);

  const fetchDropdownData = async () => {
    try {
      // Fetch platforms
      const platformsResponse = await ApiService({
        method: "GET",
        endpoint: apiPaths.platforms,
      });
      setPlatforms(platformsResponse || []);

      // Fetch interests
      const interestsResponse = await ApiService({
        method: "GET",
        endpoint: apiPaths.interests,
      });
      setInterests(interestsResponse || []);

      // Fetch services
      const servicesResponse = await ApiService({
        method: "GET",
        endpoint: apiPaths.services,
      });
      setServices(servicesResponse || []);

      // Fetch output statuses
      const outputStatusesResponse = await ApiService({
        method: "GET",
        endpoint: apiPaths.outputStatus,
      });
      setOutputStatuses(outputStatusesResponse || []);

      // Fetch users
      const usersResponse = await ApiService({
        method: "GET",
        endpoint: apiPaths.getAllEmployees,
      });
      setUsers(usersResponse || []);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  //   const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  //     const { name, value } = e.target;
  //     console.log('handleChange called:', name, value); // Debug log
  //     setFormData(prev => {
  //       const newData = {
  //         ...prev,
  //         [name]: value
  //       };
  //       console.log('New form data:', newData); // Debug log
  //       return newData;
  //     });
  //   };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get current user ID from token
      const token = localStorage.getItem("accessToken");
      if (!token) {
        toast.error("User not authenticated");
        return;
      }

      // Decode JWT token to get user ID
      let userId;
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        userId = payload.userId;
      } catch (error) {
        console.error("Error decoding token:", error);
        toast.error("Error getting user information");
        return;
      }

      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.contactCreate,
        data: {
          ...formData,
          userId: userId,
          isClient: true,
          interest: formData.interest,
          outputStatus: formData.outputStatus,
          services: formData.services,
          platform: formData.platform,
          projects: [],
          status: "Active",
          contractDetails: {},
          billingInfo: {},
          contacts: [],
          documents: [],
          messages: [],
        },
      });

      // Check if response exists and has the expected structure
      if (
        response &&
        (response.success === true ||
          response.status === "success" ||
          response.statusCode === 200 ||
          response.statusCode === 201 ||
          response._id)
      ) {
        toast.success("Client created successfully!");
        onClose();
      } else {
        // Handle different response structures
        const errorMessage =
          response?.message ||
          response?.error ||
          response?.data?.message ||
          "Failed to create client";
        console.error("API Error Response:", response);
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error creating client:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error creating client";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-center shadow-8 bg-gray-700 bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Convert Lead to Client
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {loading && <Loader />}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
            <Input
              name="name"
              label="Client Name"
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, name: e.target.value }));
              }}
              disabled={false}
              required
            />
            <Input
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }));
              }}
              disabled={false}
            />
            <Input
              name="phone"
              label="Phone"
              type="text"
              value={formData.phone}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, phone: e.target.value }));
              }}
              disabled={false}
            />
            <Input
              name="companyName"
              label="Company Name"
              type="text"
              value={formData.companyName}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  companyName: e.target.value,
                }));
              }}
              disabled={false}
            />
            <Input
              name="linkedin"
              label="LinkedIn"
              type="text"
              value={formData.linkedin}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, linkedin: e.target.value }));
              }}
              disabled={false}
            />
            <Textarea
              name="description"
              label="Description"
              value={formData.description}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }));
              }}
              disabled={false}
            />
          </div>

          <div className="flex justify-end space-x-4">
            <CancelButton
              onClick={onClose}
              className="px-6 py-2"
              children="Cancel"
              type="button"
            />
            <FormButton
              className="px-6 py-2"
              children="Convert to Client"
              type="submit"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConvertToClientModal;
